import dayjs from 'dayjs';
import { useEffect } from 'react';

import { WeekDay } from '@cbo/shared-library';
import { QueryObserverResult } from '@tanstack/react-query';

import defaultWorkWeekConfig from '../../../config/workWeekConfig';
import { useUsers } from '../../../contexts/userContext';
import { useOrgContext } from '../../../org/CommonComponentWrapper';
import { useWorkWeekConfigQuery } from '../requests/queries';
import { WorkWeekConfig, WorkWeekConfigResponse } from '../types';

export interface SchedulingOffsetLocaleResponse {
  weekStartDayOffset: number | undefined;
  loadingWorkWeekStart: boolean;
  weekStartTimeOffset: string;
  refetchWorkWeekConfig: () => Promise<QueryObserverResult<void | WorkWeekConfig | WorkWeekConfigResponse, unknown>>;
}

export default function useSchedulingOffsetLocale(): SchedulingOffsetLocaleResponse {
  const user = useUsers();
  const { organization } = useOrgContext();
  const adapterLocale = dayjs.locale();

  const {
    data,
    isLoading: loadingWorkWeekStart,
    refetch: refetchWorkWeekConfig,
  } = useWorkWeekConfigQuery(user.fullyAuthenticated === 'authenticated');

  let weekStartDayOffset: number | undefined;
  let weekStartTimeOffset = defaultWorkWeekConfig.workdayStartTime;
  if (typeof data?.dayOfWeekStart === 'string') {
    weekStartDayOffset = Number(WeekDay[data?.dayOfWeekStart as keyof typeof WeekDay]);
    const workWeekData = data as WorkWeekConfigResponse;
    weekStartTimeOffset = workWeekData?.workdayStartTime ?? defaultWorkWeekConfig.workdayStartTime;
  } else {
    weekStartDayOffset = data?.dayOfWeekStart;
  }

  useEffect(() => {
    // Clone current locale and offset weekStart so schedule can have an isolated/different dayjs weekStart/weekEnd
    const localeObject = dayjs.Ls[adapterLocale];
    dayjs.locale('schedule-offset', { ...localeObject, name: 'schedule-offset', weekStart: weekStartDayOffset });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapterLocale, data]);

  useEffect(() => {
    refetchWorkWeekConfig();
  }, [organization, refetchWorkWeekConfig]);

  return {
    weekStartDayOffset,
    loadingWorkWeekStart,
    weekStartTimeOffset,
    refetchWorkWeekConfig,
  };
}
